import { CloudUpload, Pencil } from 'lucide-react';
import { type DragEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { env } from '@/constants/env';

import { useModal } from '../../../../../components/modals/new/use-modal';
import { MediaLibraryModal } from '../../../../image-rendering/components/media-library-modal';
import type { ImageToVideoModel } from '../../../hooks/useImageToVideoActions';
import SectionCard from '../../common/sectionCard';

type Slot = 'start' | 'end';

type SourceSectionProps = {
  model: ImageToVideoModel;
};

type SlotImage = {
  id: string;
  url: string;
};

type ImageSlotProps = {
  label: string;
  currentImage?: SlotImage | undefined;
  estateId?: string | null;
  projectId?: string | null;
  onUploadClick: () => void;
  onSelectFromMediaLibraryClick: () => void;
  onDropFile: (file: File) => void;
  showHint: boolean;
};

const ImageSlot = ({
  label,
  currentImage,
  estateId,
  projectId,
  onUploadClick,
  onSelectFromMediaLibraryClick,
  onDropFile,
  showHint,
}: ImageSlotProps) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver: DragEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave: DragEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop: DragEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    // Only accept images
    if (!file.type.startsWith('image/')) {
      return;
    }

    onDropFile(file);
  };

  return (
    <div className="space-y-2">
      <div className="rounded-md p-2">
        <button
          type="button"
          className={`flex h-24 w-full cursor-pointer items-center justify-center overflow-hidden rounded-sm bg-gray-100 outline-none ${
            isDragging ? 'border-2 border-primary-light bg-primary-lighter/10' : ''
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={onUploadClick}
        >
          {currentImage ? (
            <img src={currentImage.url} alt={label} className="h-full w-full object-cover" />
          ) : showHint ? (
            <div className="flex h-full w-full flex-col items-center justify-center text-center text-xs text-gray-400">
              <CloudUpload className="mb-2 h-6 w-6 text-gray-400" />
              <span className="text-[11px] font-medium text-gray-500">
                {t('imageSlot.helperText', 'Click to upload or drag and drop')}
              </span>
              <span className="mt-1 text-[10px] text-gray-400">
                {t('imageSlot.supportedFormats', '.png, .jpeg, .jpg')}
              </span>
            </div>
          ) : null}
        </button>

        <div className="mt-2 flex flex-col items-center gap-2">
          <span className="text-xs font-semibold text-black">{label}</span>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="flex h-8 w-full max-w-[150px] items-center justify-center gap-2 px-3 text-sm text-primary-light"
              >
                <Pencil className="size-4" />
                <span>{t('replace')}</span>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              className="min-w-[180px] bg-white text-xs"
              align="center"
              side="bottom"
            >
              <DropdownMenuItem
                className="cursor-pointer"
                onSelect={() => {
                  onUploadClick();
                }}
              >
                {t('upload')}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer"
                disabled={!estateId && !projectId}
                onSelect={() => {
                  if (!estateId && !projectId) return;
                  onSelectFromMediaLibraryClick();
                }}
              >
                {t('selectFromMediaLibrary')}
              </DropdownMenuItem>

              <DropdownMenuItem className="cursor-pointer text-red-500 hover:text-red-500">
                {t('cancel')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

const SourceSection = ({ model }: SourceSectionProps) => {
  const { t } = useTranslation();
  const { style, actions } = model;
  const startSlotModal = useModal();
  const endSlotModal = useModal();

  const startInputRef = useRef<HTMLInputElement | null>(null);
  const endInputRef = useRef<HTMLInputElement | null>(null);
  const localBlobUrlsRef = useRef<Set<string>>(new Set());

  const startImage = style.images.find((img) => img.id === style.startImageId);
  const endImage = style.images.find((img) => img.id === style.endImageId);

  const handleFileForSlot = useCallback(
    (file: File, slot: Slot) => {
      const url = URL.createObjectURL(file);
      localBlobUrlsRef.current.add(url);
      const id = `local-${slot}-${Date.now()}`;

      actions.setSlotImage(slot, { id, url });
    },
    [actions]
  );

  // Cleanup any locally created blob URLs on unmount
  useEffect(
    () => () => {
      localBlobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      localBlobUrlsRef.current.clear();
    },
    []
  );

  const handleFileSelected = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, slot: Slot) => {
      const file = event.target.files?.[0];
      if (!file) return;

      handleFileForSlot(file, slot);

      // Allow selecting the same file again
      event.target.value = '';
    },
    [handleFileForSlot]
  );

  const triggerUpload = (slot: Slot) => {
    if (slot === 'start') {
      startInputRef.current?.click();
    } else {
      endInputRef.current?.click();
    }
  };

  return (
    <>
      <SectionCard title={t('source')}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ImageSlot
            label={t('start-image')}
            currentImage={startImage}
            estateId={style.estateId}
            projectId={style.projectId}
            onUploadClick={() => triggerUpload('start')}
            onSelectFromMediaLibraryClick={startSlotModal.open}
            onDropFile={(file) => handleFileForSlot(file, 'start')}
            showHint={!startImage}
          />

          <ImageSlot
            label={t('end-image')}
            currentImage={endImage}
            estateId={style.estateId}
            projectId={style.projectId}
            onUploadClick={() => triggerUpload('end')}
            onSelectFromMediaLibraryClick={endSlotModal.open}
            onDropFile={(file) => handleFileForSlot(file, 'end')}
            showHint={!endImage}
          />
        </div>

        <input
          ref={startInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => handleFileSelected(event, 'start')}
        />
        <input
          ref={endInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(event) => handleFileSelected(event, 'end')}
        />
      </SectionCard>
      <MediaLibraryModal
        modal={startSlotModal}
        onSaveSelected={(image) => {
          if (!image?.mediaId) return;

          const url = `${env.API_URL}/images/${image.mediaId}`;
          actions.setSlotImage('start', { id: image.mediaId, url });
        }}
        {...(style.estateId
          ? { type: 'estate', estateId: style.estateId }
          : { type: 'project', projectId: style.projectId ?? '' })}
      />
      <MediaLibraryModal
        modal={endSlotModal}
        onSaveSelected={(image) => {
          if (!image?.mediaId) return;

          const url = `${env.API_URL}/images/${image.mediaId}`;
          actions.setSlotImage('end', { id: image.mediaId, url });
        }}
        {...(style.estateId
          ? { type: 'estate', estateId: style.estateId }
          : { type: 'project', projectId: style.projectId ?? '' })}
      />
    </>
  );
};

export default SourceSection;
